import { AlertCircle, LoaderCircle, RefreshCw, Sparkles } from "lucide-react"

import { DealAskRobPanel } from "@/components/deals/DealAskRobPanel"
import { AiCreditUsageLink } from "@/components/shared/AiCreditUsageLink"
import { Button } from "@/components/ui/button"
import { useDealAskRob } from "@/hooks/use-deal-ask-rob"
import { cn } from "@/lib/utils"

const REVIEW_SECTIONS = [
  ["currentPosition", "Current Position"],
  ["commercialJudgement", "Commercial Judgement"],
  ["biggestOpportunity", "Biggest Opportunity"],
  ["biggestRisk", "Biggest Risk"],
  ["recommendedNextStep", "Recommended Next Step"],
]

const REVIEWED_AT_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "medium",
  timeStyle: "short",
})
const INTERNAL_SERVER_ERROR = "Internal Server Error"
const REVIEW_FAILURE_MESSAGE = "We couldn’t generate Rob’s view right now. Please try again."

function formatReviewedAt(value) {
  if (!value) return null

  const reviewedAt = new Date(value)

  return Number.isNaN(reviewedAt.getTime()) ? null : REVIEWED_AT_FORMATTER.format(reviewedAt)
}

function reviewActionState(isReviewing, isOutOfDate) {
  if (isReviewing) {
    return { Icon: LoaderCircle, iconClassName: "animate-spin", label: "Reviewing Deal" }
  }
  if (isOutOfDate) {
    return { Icon: RefreshCw, iconClassName: undefined, label: "Refresh Rob's View" }
  }

  return { Icon: Sparkles, iconClassName: undefined, label: "Get Rob's View" }
}

function reviewErrorMessage(error) {
  return error === INTERNAL_SERVER_ERROR ? REVIEW_FAILURE_MESSAGE : error
}

export function DealAskRobReviewCard({ askRob }) {
  const hasReview = Boolean(askRob.review?.sections)
  const isOutOfDate = askRob.review?.status === "out_of_date"
  const reviewedAt = formatReviewedAt(askRob.review?.reviewedAt)
  const {
    Icon: ReviewActionIcon,
    iconClassName: reviewActionIconClassName,
    label: reviewActionLabel,
  } = reviewActionState(askRob.isReviewing, isOutOfDate)
  const conversationAskRob = {
    ...askRob,
    messages: askRob.messages.filter(
      (message) => message.metadata?.reviewType !== "deal_overview",
    ),
  }

  return (
    <section className="rounded-xl border border-border bg-card p-6 text-card-foreground shadow-sm">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <img
            src="/rob_headshot.jpeg"
            alt="Rob"
            className="h-10 w-10 shrink-0 rounded-full object-cover"
          />
          <div>
            <h2 className="text-lg font-bold text-foreground">Ask Rob</h2>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              Bring Rob into this deal for an expert review, commercial judgement and coaching.
            </p>
          </div>
        </div>

        {hasReview && !isOutOfDate ? (
          <span className="inline-flex shrink-0 items-center gap-2 rounded-md border border-primary/20 bg-primary/10 px-3 py-2 text-xs font-bold uppercase tracking-widest text-primary">
            <Sparkles className="h-4 w-4" />
            Current
          </span>
        ) : (
          <Button
            type="button"
            disabled={!askRob.canUsePersistedDealApi || askRob.isLoading || askRob.isReviewing}
            onClick={() => void askRob.generateReview()}
            className="shrink-0"
          >
            <ReviewActionIcon className={reviewActionIconClassName} />
            {reviewActionLabel}
          </Button>
        )}
      </div>

      {isOutOfDate ? (
        <div className="mt-5 flex items-start gap-2 rounded-lg border border-chart-4/30 bg-chart-4/10 px-4 py-3 text-sm text-foreground">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-chart-4" />
          <div>
            <p>Deal information has changed since Rob&apos;s last review. Refresh to update his view.</p>
            {reviewedAt ? (
              <p className="mt-1 text-xs text-muted-foreground">Rob&apos;s view from {reviewedAt}.</p>
            ) : null}
          </div>
        </div>
      ) : null}

      {askRob.reviewError ? (
        <div className="mt-5 flex items-start justify-between gap-4 rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <p>
            {reviewErrorMessage(askRob.reviewError)} <AiCreditUsageLink error={askRob.reviewError} />
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={askRob.isReviewing}
            onClick={() => void askRob.generateReview()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      {hasReview ? (
        <div className="mt-6">
          <dl className="grid border-y border-border md:grid-cols-2">
            {REVIEW_SECTIONS.map(([key, label], index) => (
              <div
                key={key}
                className={cn(
                  "border-border py-5 md:px-5",
                  index > 0 && "border-t md:border-t-0",
                  index % 2 === 1 && "md:border-l",
                  index >= 2 && "md:border-t",
                  index === REVIEW_SECTIONS.length - 1 && "md:col-span-2 md:border-l-0",
                )}
              >
                <dt className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {label}
                </dt>
                <dd className="mt-2 text-sm font-medium leading-relaxed text-foreground">
                  {askRob.review.sections[key]}
                </dd>
              </div>
            ))}
          </dl>

          <p className="mt-5 text-sm font-medium text-foreground">
            {askRob.review.sections.conversationPrompt}
          </p>

          <div
            aria-label="Ask Rob coaching conversation"
            role="region"
            className="mt-5 flex h-[min(28rem,60vh)] min-h-80 flex-col overflow-hidden border-t border-border pt-5"
          >
            <h3 className="mb-3 shrink-0 text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Conversation History
            </h3>
            <div className="min-h-0 flex-1">
              <DealAskRobPanel askRob={conversationAskRob} fullHeight />
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}

export function DealAskRobReviewCardForDeal({ deal, onSuggestionAccepted }) {
  const askRob = useDealAskRob({ deal, tab: "overview", onSuggestionAccepted })

  return <DealAskRobReviewCard askRob={askRob} />
}
